import { hasValue } from '../components/PreviewCards/utils';

import type {
  EmailContactAddress,
  EmailContactEmail,
  EmailContactPhone,
  EmailJob,
  EmailPotentialOwner,
  EmailReportData,
  EmailSocialProfile,
} from '../types';

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function toString(value: unknown): string {
  return value == null ? '' : String(value);
}

function toBooleanFlag(value: unknown): string {
  if (typeof value === 'boolean') {
    return value ? 'yes' : 'no';
  }
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    if (['true', '1', 'yes', 'y'].includes(lower)) {
      return 'yes';
    }
    if (['false', '0', 'no', 'n'].includes(lower)) {
      return 'no';
    }
  }
  return '';
}

const KNOWN_PLATFORMS: Record<string, string> = {
  'linkedin.com': 'LinkedIn',
  'facebook.com': 'Facebook',
  'twitter.com': 'Twitter',
  'x.com': 'X',
  'instagram.com': 'Instagram',
  'pinterest.com': 'Pinterest',
  'youtube.com': 'YouTube',
  'tiktok.com': 'TikTok',
  'github.com': 'GitHub',
};

function platformFromUrl(url: string): string {
  try {
    const host = new URL(url).hostname.replace(/^www\./, '');
    for (const [domain, name] of Object.entries(KNOWN_PLATFORMS)) {
      if (host.includes(domain)) {
        return name;
      }
    }
    const label = host.split('.')[0] ?? '';
    return label.charAt(0).toUpperCase() + label.slice(1);
  } catch {
    return '';
  }
}

function displayPathFromUrl(url: string): string {
  try {
    const { hostname, pathname } = new URL(url);
    const host = hostname.replace(/^www\./, '');
    const path = pathname.replace(/\/$/, '');
    return path ? `${host}${path}` : host;
  } catch {
    return url;
  }
}

function mapPotentialOwner(raw: unknown): EmailPotentialOwner | null {
  const record = asRecord(raw);
  if (!record || !record.name) {
    return null;
  }
  return {
    name: toString(record.name),
    bvId: toString(record.bv_id ?? record.bvId),
    primaryAddress: toString(record.primary_address ?? record.primaryAddress),
    primaryPhone: toString(record.primary_phone ?? record.primaryPhone),
    primaryEmail: toString(record.primary_email ?? record.primaryEmail),
  };
}

/**
 * An email report is "empty" when deliverability is poor (low / none / unknown)
 * and there is nothing substantive to show: no owner and no linked records.
 * The fraud / validity / spam status chips are treated as noise, not information,
 * so a poor-deliverability email with only those is hidden. Used to suppress
 * empty deepsearch previews.
 */
export function isEmailReportEmpty(data: EmailReportData): boolean {
  const deliverability = data.deliverability.trim().toLowerCase();
  const isPoorDeliverability =
    deliverability === '' ||
    deliverability === 'low' ||
    deliverability === 'poor' ||
    deliverability === 'none' ||
    deliverability === 'unknown';

  const hasOwner =
    data.potentialOwner != null ||
    data.otherPotentialOwners.length > 0 ||
    data.ownersCount > 0 ||
    hasValue(data.ownerName) ||
    hasValue(data.primaryAddress) ||
    hasValue(data.primaryPhone) ||
    hasValue(data.primaryEmail);

  const hasLinkedRecords =
    data.potentialAddresses.length > 0 ||
    data.potentialEmails.length > 0 ||
    data.potentialPhoneNumbers.length > 0 ||
    data.socialProfiles.length > 0 ||
    data.jobs.length > 0 ||
    data.education.length > 0;

  return isPoorDeliverability && !hasOwner && !hasLinkedRecords;
}

export function mapEmailReportPayload(raw: unknown): EmailReportData {
  const r = asRecord(raw) ?? {};

  const contactDetails =
    asRecord(r.contactDetails) ?? asRecord(r.contact_details) ?? {};
  const ownerDetails =
    asRecord(r.ownerDetails) ?? asRecord(r.owner_details) ?? {};

  const potentialAddresses: EmailContactAddress[] = asArray(
    contactDetails.potential_addresses
  )
    .map((a) => {
      const item = asRecord(a);
      return { full: toString(item?.full ?? item?.address ?? a) };
    })
    .filter((a) => Boolean(a.full));

  const potentialEmails: EmailContactEmail[] = asArray(
    contactDetails.potential_emails
  )
    .map((e) => {
      const item = asRecord(e);
      return {
        address: toString(item?.address),
        type: toString(item?.type),
      };
    })
    .filter((e) => Boolean(e.address));

  const potentialPhoneNumbers: EmailContactPhone[] = asArray(
    contactDetails.potential_phone_numbers
  )
    .map((p) => {
      const item = asRecord(p);
      return {
        number: toString(item?.number),
        type: toString(item?.type),
      };
    })
    .filter((p) => Boolean(p.number));

  const socialProfiles: EmailSocialProfile[] = asArray(
    ownerDetails.social_profiles
  )
    .map((s) => {
      const item = asRecord(s);
      if (item) {
        return {
          platform: toString(item.platform ?? item.name),
          handle: toString(item.handle),
          url: toString(item.url),
        };
      }
      // plain URL string
      const url = toString(s);
      if (!url) {
        return null;
      }
      return {
        platform: platformFromUrl(url),
        handle: displayPathFromUrl(url),
        url,
      };
    })
    .filter((s): s is EmailSocialProfile => s !== null && Boolean(s.platform));

  const jobs: EmailJob[] = asArray(ownerDetails.jobs)
    .map((j) => {
      const item = asRecord(j);
      if (!item) {
        return null;
      }
      return {
        company: toString(item.company),
        period: toString(item.period),
        titles: asArray(item.titles).map(toString).filter(Boolean),
      };
    })
    .filter((j): j is EmailJob => j !== null && Boolean(j.company));

  const education: string[] = asArray(ownerDetails.education)
    .map((e) => {
      const item = asRecord(e);
      return item ? toString(item.school ?? item.name ?? e) : toString(e);
    })
    .filter(Boolean);

  const potentialOwner = mapPotentialOwner(
    r.potentialOwner ?? r.potential_owner
  );

  const otherPotentialOwners = asArray(
    r.otherPotentialOwners ?? r.other_potential_owners
  )
    .map(mapPotentialOwner)
    .filter((o): o is EmailPotentialOwner => o !== null);

  const ownersCountRaw = Number(r.ownersCount ?? r.owners_count);
  const ownersCount =
    Number.isFinite(ownersCountRaw) && ownersCountRaw >= 0
      ? Math.round(ownersCountRaw)
      : 0;

  return {
    email: toString(r.email),
    emailType: toString(r.emailType ?? r.email_type),
    validEmail: toBooleanFlag(r.validEmail ?? r.valid_email),
    fraudScore: toString(r.fraudScore ?? r.fraud_score),
    deliverability: toString(r.deliverability),
    recentAbuse: toBooleanFlag(r.recentAbuse ?? r.recent_abuse),
    isLeaked: toBooleanFlag(r.isLeaked ?? r.is_leaked),
    spamTrapScore: toString(r.spamTrapScore ?? r.spam_trap_score),
    isFrequentComplainer: toBooleanFlag(
      r.isFrequentComplainer ?? r.is_frequent_complainer
    ),
    ownersCount,
    ownerName: toString(r.ownerName ?? r.owner_name),
    primaryAddress: toString(r.primaryAddress ?? r.primary_address),
    primaryPhone: toString(r.primaryPhone ?? r.primary_phone),
    primaryEmail: toString(r.primaryEmail ?? r.primary_email),
    potentialOwner,
    otherPotentialOwners,
    potentialAddresses,
    potentialEmails,
    potentialPhoneNumbers,
    socialProfiles,
    jobs,
    education,
  };
}
